import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import i18next from "src/i18n/config";
import { makeStyles } from "tss-react/mui";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import ApmLink from "@shared/ApmLink";
import { PrimaryOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

import { useAccountProfile } from "../../hooks/useAccountProfile";

import { PasswordChangeForm } from "./PasswordChangeForm";
import { TwoFactorDisableForm } from "./TwoFactorDisableForm";
import { TwoFactorEnableForm } from "./TwoFactorEnableForm";

const useStyles = makeStyles()((theme: Theme) => ({
    switchLabel: {
        marginBottom: theme.spacing(1),
        fontWeight: 600,
    },
    switchContent: { display: "flex", flexDirection: "column" },
    settingsRows: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    settingsRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        gap: theme.spacing(2),
    },
}));

function AccountSecurityPage() {
    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useStyles();
    const { profile, isLoading, refetch } = useAccountProfile();

    const [searchParams, setSearchParams] = useSearchParams();
    const mode = searchParams.get("mode");

    const clearMode = useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);
    const handleTwoFactorSuccess = useCallback(() => {
        clearMode();
        void refetch();
    }, [clearMode, refetch]);

    return (
        <PageContainer fullWidth={false} isLoading={isLoading}>
            {mode === "password" ? (
                <PasswordChangeForm onCancel={clearMode} />
            ) : mode === "2fa-enable" ? (
                <TwoFactorEnableForm onSuccess={handleTwoFactorSuccess} />
            ) : mode === "2fa-disable" ? (
                <TwoFactorDisableForm onSuccess={handleTwoFactorSuccess} />
            ) : (
                <SectionPanel title={t("pages:settingsPage.accountSecurity.title")}>
                    <Box className={classes.settingsRows}>
                        <Box className={classes.settingsRow}>
                            <Box className={classes.switchContent}>
                                <span className={classes.switchLabel}>
                                    {t("pages:settingsPage.accountSecurity.accountPassword")}
                                </span>
                                <div>{t("pages:settingsPage.accountSecurity.accountPasswordDescription")}</div>
                            </Box>
                            <ApmLink
                                parent="Account security settings"
                                title={t("common:button.change")}
                                role="button"
                                to="?mode=password"
                            >
                                <PrimaryOutlinedButton size="small" startIcon={<i className="fa-solid fa-fw fa-key" />}>
                                    {t("common:button.change")}
                                </PrimaryOutlinedButton>
                            </ApmLink>
                        </Box>
                        <Box className={classes.settingsRow}>
                            <Box className={classes.switchContent}>
                                <span className={classes.switchLabel}>{t("pages:settingsPage.accountSecurity.twoFactor")}</span>
                                <div>{t("pages:settingsPage.accountSecurity.twoFactorDescription")}</div>
                            </Box>
                            {profile?.twoFactorEnabled ? (
                                <ApmLink
                                    parent="Account security settings"
                                    title={t("common:button.disable")}
                                    role="button"
                                    to="?mode=2fa-disable"
                                >
                                    <SecondaryButton size="small">{t("common:button.disable")}</SecondaryButton>
                                </ApmLink>
                            ) : (
                                <ApmLink
                                    parent="Account security settings"
                                    title={t("common:button.enable")}
                                    role="button"
                                    to="?mode=2fa-enable"
                                >
                                    <PrimaryOutlinedButton size="small" startIcon={<i className="fa-solid fa-shield-halved" />}>
                                        {t("common:button.enable")}
                                    </PrimaryOutlinedButton>
                                </ApmLink>
                            )}
                        </Box>
                    </Box>
                </SectionPanel>
            )}
        </PageContainer>
    );
}

export default withMetaDescription(AccountSecurityPage, {
    title: i18next.t("pages:settingsPage.pages.security.metaTitle"),
});
